import { useState, useEffect, useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Modal, Checkbox, Space, Button, message, Typography } from 'antd';

import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError } from '../../clientUtils';

const { Paragraph } = Typography;

const LegalNoticeModal = () => {
  const { isAuthenticated } = useAuth0();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [noticeData, setNoticeData] = useState(null);

  const handleCheckbox = (e) => {
    setChecked(e.target.checked);
  };

  const checkLegalNoticeStatus = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      await CallApi(
        apiConfig.legalNotice.check,
        null,
        null,
        null,
        (data) => {
          if (data?.showNotice) {
            setNoticeData(data);
            setIsOpen(true);
          }
        },
        () => {
          // Error checking legal notice status - silently fail
        }
      );
    } catch (error) {
      // Error in checkLegalNoticeStatus - silently fail
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkLegalNoticeStatus();
  }, [checkLegalNoticeStatus]);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const acceptData = {
        noticeId: noticeData?.noticeId,
        acceptedAt: new Date().toISOString()
      };

      await CallApi(
        apiConfig.legalNotice.accept,
        null,
        acceptData,
        setIsLoading,
        () => {
          message.success('Legal notice accepted successfully');
          setIsOpen(false);
          setChecked(false);
          setNoticeData(null);
        },
        (error) => {
          message.error(getError(error));
        }
      );
    } catch (error) {
      message.error('Failed to accept legal notice');
    } finally {
      setIsLoading(false);
    }
  };

  if (!noticeData) {
    return null;
  }

  return (
    <Modal
      closable={false}
      footer={[
        <Space key="submit" className="w-100 center">
          <Button key="accept" disabled={!checked} loading={isLoading} onClick={handleAccept} type="primary">
            Accept
          </Button>
        </Space>
      ]}
      keyboard={false}
      maskClosable={false}
      open={isOpen}
      title={noticeData?.title || ''}
      width={700}>
      <Space className="w-100" direction="vertical" size="large">
        <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px 0' }}>
          {noticeData?.content ? (
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{noticeData.content}</Paragraph>
          ) : (
            <>
              <Paragraph strong style={{ textAlign: 'center', fontSize: '16px' }}>
                TO WHOMSOEVER IT MAY CONCERN
              </Paragraph>
              <Paragraph>Dear All,</Paragraph>
              <Paragraph>
                We wish to inform you that Mensa Brand Technologies Private Limited (<strong>Mensa</strong> or{' '}
                <strong>Company</strong>) is undergoing an internal restructuring process wherein Mensa is merging with
                its group companies (identified below) pursuant to a scheme of arrangement. The scheme of arrangement
                has received the requisite approval from National Company Law Tribunal, Chandigarh Bench (
                <strong>Merger</strong>).
              </Paragraph>
              <Paragraph>
                Under the proposed Merger, the following group companies (collectively, the{' '}
                <strong>Merging Companies</strong>) are proposed to be amalgamated into Mensa as part of an internal
                restructuring to consolidate operations and enhance operational efficiencies.
              </Paragraph>
              <div style={{ marginBottom: '16px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Sr.no</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                        Merging Company name
                      </th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Products / Brands</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>1</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        Archernar Brand Technologies Private Limited
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>UrbanFix, Bulfyss</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>2</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        Implexians Eco Solutions Private Limited
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Trustbasket</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>3</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        Lepus Brand Technologies Private Limited
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>BonKids, Bonorganiks</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>4</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Villain Lifestyle Private Limited</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Villain</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>5</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        Pyxis Brand Technologies Private Limited
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Karagiri</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>6</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Helea Technology Private Limited</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>Helea</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>7</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        Cephus Brand Technologies Private Limited
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Paragraph>
                Upon the Merger becoming effective, all assets, rights, obligations, liabilities, contracts, trademark,
                intellectual property rights and operations of the Merging Companies stand transferred to and vest in
                Mensa. The above-mentioned Brands will thereafter continue to operate under Mensa. Accordingly, we wish
                to formally apprise you of certain forthcoming operational changes in connection with the Merger, to
                ensure your preparedness for the same.
              </Paragraph>
              <Paragraph>
                Kindly note that the effective date of the Merger is 5th March 2026 (<strong>Effective Date</strong>).
              </Paragraph>
              <Paragraph>
                Please be assured that there will be no changes to any ongoing working arrangements with your points of
                contact. To operationally implement the Merger and fast track the changeover, with effect from the
                Effective Date, we request you to onboard Mensa Brand Technologies Private Limited as a customer in your
                internal systems.
              </Paragraph>
              <Paragraph>
                We understand that this may require you to complete a Know Your Customer process. To facilitate the
                same, we are sharing key company identifiers, including PAN and GST registration details, along with
                this email.
              </Paragraph>
              <Paragraph>
                Pursuant to the Merger, existing contracts executed with the Merging Companies will be novated in favour
                of Mensa. The contractual terms will remain unchanged, save and except for the substitution of the
                contracting entity name. Further, please note that the lower tax certificates issued in the name of the
                Merging Companies would no longer remain valid post-merger. Accordingly, Mensa would be able to extend
                the benefit of lower tax deduction only upon issuance and availability of a valid lower tax certificate
                in the name of Mensa, and to the extent of the limit specified in such certificate.
              </Paragraph>
              <Paragraph>
                Please submit all pending invoices by 03rd March 2026, and from the effective date onward, kindly issue
                all invoices in the name of <strong>Mensa Brand Technologies Private Limited</strong>.
              </Paragraph>
              <Paragraph>
                We will reach out to you separately with the necessary documentation to formalise the novation.
              </Paragraph>
              <Paragraph>
                Should you have any questions or require any additional documentation, please feel free to reach out to
                your existing point of contact or write to us at{' '}
                <a href="mailto:secretarial@mensabrands.com">secretarial@mensabrands.com</a> or{' '}
                <a href="mailto:communications@mensabrands.com">communications@mensabrands.com</a>.
              </Paragraph>
              <Paragraph>We appreciate your continued support and co-operation.</Paragraph>
              <Paragraph>
                <strong>Warm regards,</strong>
                <br />
                <strong>For: Mensa Brand Technologies Private Limited</strong>
              </Paragraph>
              <Paragraph strong style={{ marginTop: '20px' }}>
                Key Company Information:
              </Paragraph>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '13px' }}>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', width: '40%' }}>
                      Company Name:
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      Mensa Brand Technologies Private Limited
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>
                      Company Registration No.:
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>U14101HR2021PTC127302</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>TAN</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>BLRM37385F</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>
                      Permanent Account Number
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>AAOCM5326J</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>
                      GST Registration No.:
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Refer the table below</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>IEC No.:</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>AAOCM5326J</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>
                      Registered Address:
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                      Inno House, 2nd Floor, Plot No. 9 (4 Bay), Sector 32, Gurgaon, Sadar Bazar, Gurgaon- 122001,
                      Haryana, India
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>
                      Country of Registration:
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>India</td>
                  </tr>
                </tbody>
              </table>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>State</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>GSTN</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Uttar Pradesh</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>09AAOCM5326J1Z0</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Maharashtra</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>27AAOCM5326J1Z2</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Haryana</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>06AAOCM5326J1Z6</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>West Bengal</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>19AAOCM5326J1ZZ</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Rajasthan</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>08AAOCM5326J1Z2</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Karnataka</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>29AAOCM5326J1ZY</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Telangana</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>36AAOCM5326J1Z3</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Delhi</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>07AAOCM5326J1Z4</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Madhya Pradesh</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>23AAOCM5326J1ZA</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Gujarat</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>24AAOCM5326J1Z8</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Punjab</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>03AAOCM5326J1ZC</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Andhra Pradesh</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>37AAOCM5326J1Z1</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>Tamilnadu</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>33AAOCM5326J1Z9</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>ISD</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>29AAOCM5326J2ZX</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
        <Checkbox checked={checked} onChange={handleCheckbox}>
          I have read and accept the legal notice
        </Checkbox>
      </Space>
    </Modal>
  );
};

export default LegalNoticeModal;
